import { useMediaQuery } from '@mui/material'
import cx from 'classnames'
import React, { useEffect } from 'react'

import { COUNTRY_SELECT_INPUT_VALUES } from '@js/common/constants'
import Geocoder from '@js/common/geocoder'
import { OptionItem } from '@js/common/types/OptionItem'
import User from '@js/common/user'

import CTA from '@f/elements/CTA'
import { Form } from '@f/elements/HookForm'
import yup from '@f/elements/HookForm/validator/yup'

import {
  conversionFunnelCopy,
  getConversionFromFlowInfo
} from '@f-fertighaus/applications/RequestWizard/partials/utils'
import { $TSFixMe } from '@f-fertighaus/applications/UserAccount/interfaces/utils'

import { Data } from './dataType'
import { ConversionFunnelVariant } from './UserGenderNameEmail'

interface UserAddressPhoneProps {
  data: Data
  actions: $TSFixMe
  funnelVariant: ConversionFunnelVariant
}

interface UserAddresPhoneSchema {
  street: string
  postal_code: string
  city: string
  country: string
  phone: string
  phoneCountryPrefix: string
}

const checkoutAddressPhoneValidationSchema: yup.ObjectSchema<UserAddresPhoneSchema> = yup
  .object()
  .shape({
    street: yup.string().street(),
    postal_code: yup.string().postalCode(),
    city: yup.string().city(),
    country: yup.string().required(),
    phone: yup.string().phone(),
    phoneCountryPrefix: yup.string().required()
  })

const UserAddressPhone = (props: UserAddressPhoneProps) => {
  const isMobileView = useMediaQuery('(max-width: 767px)')
  const formCountry =
    props.data.user.country ||
    (window.location.hostname.split('.').pop() === 'at' ? 'AT' : 'DE')

  useEffect(() => {
    props.actions.update_wtValue({ value: 'phone_address' })
  }, [])

  const isConversionFunnel = Boolean(props.funnelVariant)

  const handleFormSubmit = (values: UserAddresPhoneSchema) => {
    const { phoneCountryPrefix, ...valuesWithoutPhoneCountryPrefix } = values

    const submitValue = {
      ...valuesWithoutPhoneCountryPrefix,
      phone_country_prefix: phoneCountryPrefix
    }

    props.actions.handleUserDataChange({
      detail: submitValue,
      forcePostRequest: true
    })
    props.actions.move({})
  }

  const { street, postal_code, phone, phone_country_prefix } = props.data.user

  const defaultValues = {
    street: street || '',
    postal_code: postal_code || '',
    city: '',
    country: formCountry || 'DE',
    phoneCountryPrefix: phone_country_prefix || 'DE',
    phone: phone?.toString() || ''
  }

  const ctaText = isConversionFunnel
    ? conversionFunnelCopy[props.funnelVariant].step_2.ctaText
    : 'Absenden & Weiter'

  const submitButton = (
    <CTA
      type='block-primary'
      tag='button'
      buttonType='submit'
      data-sel='btn-cta'
      className='is-block:s'
      wtData={{
        'CTA-click': {
          ...props.data.trackingInfo
        }
      }}>
      {ctaText}
    </CTA>
  )

  const userHasBauort = !!User.getLocation().label

  const handlePostalCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setValue: (fieldName: string, value: string) => void
  ) => {
    const postalCode = e.target.value
    setValue('postal_code', postalCode)
    if (postalCode) {
      const geocoder = new Geocoder()
      const completePostcode = formCountry + '-' + postalCode

      geocoder
        .getLocationFromPostcode(completePostcode)
        .then(data => {
          if (/^(DE|AT)-/.test(data.postcode)) {
            props.actions.handleUserDataChange({
              detail: {
                city: data.city
              }
            })
            setValue('city', data.city)

            if (!userHasBauort) {
              props.actions.updateLocation(User.setLocation(data))
            }
          }
        })
        .catch(() => {})
    }
  }

  const disclaimerParagraph = (
    <p className='[ f-t-micro f-c-fo-neu200 f-m-b-020 f-d-bl f-t-centre ] RequestWizard-bottom'>
      {`Mit dem Absenden Ihrer Details stimmen Sie der Nutzung der Angaben gemäß unserer `}
      <CTA
        type='text-primary'
        tag='a'
        href='/datenschutz/'
        target='_blank'
        className='internal'>
        Datenschutzerklärung
      </CTA>
      {' zu.'}
    </p>
  )

  return (
    <Form.Wrapper<UserAddresPhoneSchema>
      className={cx(
        'RequestWizard-form-wrapper',
        isConversionFunnel &&
          'RequestWizard-form-wrapper--HDP_conversion-funnel'
      )}
      onSubmit={handleFormSubmit}
      defaultValues={defaultValues}
      validationSchema={checkoutAddressPhoneValidationSchema}
      wtData={{
        form: {
          location: 'phone_address',
          ...props.data.trackingInfo
        },
        formSubmit: {
          location: 'phone_address',
          indicates: [
            getConversionFromFlowInfo(
              props.data.trackingInfo.flow_information.flow_type,
              props.data._wtIndicates
            )
          ],
          ...props.data.trackingInfo
        }
      }}
      data-wtloc='phone_address'
      data-wtlab='conversion'>
      <div className='RequestWizard-top'>
        <div className='RequestWizard-heading'>
          <div className='[ f-t-tmedium f-c-fo-pri f-t-centre f-p-h-080:m ]'>
            {isConversionFunnel
              ? conversionFunnelCopy[props.funnelVariant].step_2.header
              : 'An welche Adresse dürfen Infomaterialien gesendet werden?'}
          </div>
        </div>

        <div className='RequestWizard-content'>
          <Form.Text
            name='street'
            className='[ f-m-b-020 f-c-bo-neu050 ] has-border is-full-width'
            placeholder='Straße und Hausnummer'
            inputProps={{
              'data-sel': 'ipt-strt',
              autoComplete: 'address-line1'
            }}
          />
          <div className='RequestWizard-columns-wrapper'>
            <div className='[ f-d-flr ] RequestWizard-columns'>
              <Form.ConnectForm>
                {({ setValue }) => {
                  return (
                    <>
                      <div className='RequestWizard-column'>
                        <Form.Text
                          name='postal_code'
                          className='[ f-m-b-020 f-c-bo-neu050 ] has-border'
                          placeholder='PLZ'
                          inputProps={{
                            'data-sel': 'ipt-zip',
                            autoComplete: 'postal-code',
                            onBlur: e => handlePostalCodeChange(e, setValue)
                          }}
                        />
                      </div>
                      <div className='RequestWizard-column'>
                        <Form.Text
                          name='city'
                          className='[ f-m-b-020 f-c-bo-neu050 ] has-border'
                          placeholder='Stadt'
                          inputProps={{ 'data-sel': 'ipt-city' }}
                        />
                      </div>
                    </>
                  )
                }}
              </Form.ConnectForm>
            </div>
          </div>
          <Form.Select
            className='[ f-c-ba-whi f-c-fo-pri f-m-b-020 f-c-bo-neu050 ] has-border'
            name='country'
            options={
              (COUNTRY_SELECT_INPUT_VALUES as unknown) as OptionItem<string>[]
            }
            inputProps={{ 'data-sel': 'slct-ctry' }}
          />
          <Form.Phone
            className='[ f-c-ba-whi f-c-fo-pri f-d-bl f-m-b-020 f-c-bo-neu050 ] has-border'
            phoneName='phone'
            placeholder='Handynummer'
            countryName='phoneCountryPrefix'
            inputProps={{ 'data-sel': 'ipt-pnum', inputMode: 'tel' }}
            phoneCountryPrefixProps={{ 'data-sel': 'slct-pctry' }}
          />
        </div>
        <div className='f-t-centre f-m-v-040:m f-m-b-020'>
          {isMobileView ? disclaimerParagraph : submitButton}
        </div>
      </div>
      {isMobileView ? submitButton : disclaimerParagraph}
    </Form.Wrapper>
  )
}

export default UserAddressPhone
